"use client";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import menu from "@/data/menu.json";
import formatMenu from "@/utils/formatMenu";
import { MdOutlineArrowBack, MdSearch } from "react-icons/md";
import { normalizeText } from "@/utils/convert";
import { openMenuLink } from "@/utils/openMenuLink";

const isExternalPath = (path = "") => /^https?:\/\//i.test(path);

const normalizePath = (path = "") => {
  if (!path || isExternalPath(path)) return "";

  const cleanedPath = path.split("?")[0].split("#")[0];
  return cleanedPath.length > 1 ? cleanedPath.replace(/\/+$/, "") : cleanedPath;
};

const pathMatches = (pathname = "", menuPath = "") => {
  const currentPath = normalizePath(pathname);
  const targetPath = normalizePath(menuPath);

  if (!currentPath || !targetPath) return false;
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

const hasActiveChild = (item, pathname) =>
  item.children?.some((child) => pathMatches(pathname, child.path)) ?? false;

const findActiveMenuName = (items, pathname) =>
  items.find((item) => pathMatches(pathname, item.path) || hasActiveChild(item, pathname))?.name ?? null;

const filterMenu = (items, search) => {
  if (!search.trim()) return items;

  const normalizedSearch = normalizeText(search).toLowerCase();

  return items
    .map((item) => {
      const nameMatch = normalizeText(item.name).toLowerCase().includes(normalizedSearch);
      const filteredChildren = item.children?.filter((child) =>
        normalizeText(child.name).toLowerCase().includes(normalizedSearch),
      );

      if (nameMatch) return item;
      if (filteredChildren?.length) return { ...item, children: filteredChildren };
      return null;
    })
    .filter(Boolean);
};

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const pathname = usePathname();
  const trigger = useRef(null);
  const sidebar = useRef(null);

  const formattedMenu = useMemo(() => formatMenu(menu), []);
  const [manualOpenMenuName, setManualOpenMenuName] = useState(null);
  const [search, setSearch] = useState("");

  const activeMenuName = useMemo(
    () => findActiveMenuName(formattedMenu, pathname),
    [formattedMenu, pathname],
  );

  const normalizedSearch = search.trim();

  const openMenuName = normalizedSearch ? manualOpenMenuName : manualOpenMenuName ?? activeMenuName;

  const viewMenu = useMemo(() => {
    const filteredMenu = filterMenu(formattedMenu, search);

    return filteredMenu.map((item) => ({
      ...item,
      isActive: item.name === activeMenuName,
      isOpen: normalizedSearch ? true : item.name === openMenuName,
    }));
  }, [activeMenuName, formattedMenu, normalizedSearch, openMenuName, search]);

  const toggleMenu = useCallback(
    (name) => {
      setManualOpenMenuName((prev) => {
        if (prev === name) {
          return name === activeMenuName ? null : activeMenuName;
        }

        return name;
      });
    },
    [activeMenuName],
  );

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      ) {
        return;
      }
      setSidebarOpen(false);
    };

    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [setSidebarOpen, sidebarOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ key }) => {
      if (!sidebarOpen || key !== "Escape") return;
      setSidebarOpen(false);
    };

    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [setSidebarOpen, sidebarOpen]);

  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-9999 flex h-screen w-72.5 flex-col overflow-y-hidden bg-base-100 shadow-sm duration-300 ease-linear dark:bg-dark-base-100 lg:static lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* <!-- SIDEBAR HEADER --> */}
      <div className="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5">
        <Link href="/" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <Image
            width={268}
            height={112}
            src="/images/logo/Marca CJ_6.png"
            alt="Logo"
            priority
          />
        </Link>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="block text-slate-700 dark:text-white lg:hidden"
        >
          <MdOutlineArrowBack size={25} />
        </button>
      </div>
      {/* <!-- SIDEBAR HEADER --> */}

      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        {/* <!-- Sidebar Menu --> */}
        <nav className="mt-5 px-4 py-4 lg:mt-9 lg:px-6">
          {/* <!-- Menu Group --> */}
          <div>
            <div className="group mx-4 mb-8 flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 text-slate-600 focus-within:border-primary focus-within:text-primary dark:border-form-strokedark dark:bg-form-input dark:text-bodydark1">
              <input
                placeholder="Buscar"
                className="m-0 w-full appearance-none border-none bg-transparent text-sm placeholder:text-slate-400 focus:outline-none focus:ring-0 focus:shadow-none dark:placeholder:text-bodydark2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <MdSearch className="text-2xl font-bold" />
            </div>

            <h3 className="mb-4 ml-4 text-sm font-semibold text-slate-500 dark:text-bodydark2">
              MENU
            </h3>

            <ul className="mb-6 flex flex-col gap-1.5">
              {viewMenu.map((i) => {
                const Icon = i.icon;
                const parentClasses = i.isActive
                  ? "bg-primary/10 text-primary dark:bg-meta-4 dark:text-white"
                  : "text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-bodydark1 dark:hover:bg-meta-4 dark:hover:text-white";

                return (
                  <li key={i.name}>
                    <Link
                      href="#"
                      className={`group relative flex items-center gap-2.5 rounded-lg px-4 py-2.5 font-medium duration-300 ease-in-out ${parentClasses}`}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleMenu(i.name);
                      }}
                    >
                      {typeof Icon === "function" ? <Icon className="shrink-0" /> : null}
                      <span className="pr-6">{i.name}</span>
                      <svg
                        className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current duration-200 ${
                          i.isOpen ? "rotate-180" : "rotate-0"
                        }`}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                          fill=""
                        />
                      </svg>
                    </Link>

                    {/* <!-- Dropdown Menu Start --> */}
                    <div
                      className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
                        i.isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <ul className="mt-2 flex min-h-0 flex-col gap-1 overflow-hidden border-l border-slate-200 pb-2 pl-4 dark:border-form-strokedark">
                        {i.children.map((j) => {
                          const isExternal = isExternalPath(j.path);
                          const isChildActive = pathMatches(pathname, j.path);
                          const childClasses = isChildActive
                            ? "bg-primary/10 text-primary font-semibold dark:bg-meta-4 dark:text-white"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-bodydark2 dark:hover:bg-meta-4 dark:hover:text-green";

                          return (
                            <li key={`${i.name}-${j.name}`}>
                              {isExternal ? (
                                <a
                                  href={j.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`group relative flex items-center gap-2.5 rounded-md px-4 py-2 text-sm duration-300 ease-in-out ${childClasses}`}
                                  onClick={async (e) => {
                                    e.preventDefault();

                                    await openMenuLink({
                                      to: j.path,
                                      newTab: true,
                                      onError: (err) => console.error(err),
                                    });

                                    setSidebarOpen(false);
                                  }}
                                >
                                  {j.name}
                                </a>
                              ) : (
                                <Link
                                  href={j.path}
                                  className={`group relative flex items-center gap-2.5 rounded-md px-4 py-2 text-sm duration-300 ease-in-out ${childClasses}`}
                                  onClick={() => setSidebarOpen(false)}
                                >
                                  {j.name}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;

Sidebar.propTypes = {
  sidebarOpen: PropTypes.bool.isRequired,
  setSidebarOpen: PropTypes.func.isRequired,
};
